import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'lincd-mui-base/lib/components/Modal';
import { useTranslate } from '@tolgee/react';
import './CityModal.scss';
import style from './CityModal.scss.json';
import { cl } from 'lincd/lib/utils/ClassNames';

interface CityModalProps {
  cityURI: string;
  cityName: string;
  cityStats: Map<string, number> | null;
  onClose: () => void;
  open: boolean;
}

export const CityModal: React.FC<CityModalProps> = ({
  cityURI,
  cityName,
  cityStats,
  onClose,
  open,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef(null);
  const { t } = useTranslate();

  const [chartReady, setChartReady] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (!chartRef.current) {
      console.warn('chartRef.current is null!');
      return;
    }

    // check if cityStats is empty
    if (!cityStats || cityStats.size === 0) {
      setChartReady(true);
      return;
    }

    const loadChart = async () => {
      setChartReady(false);

      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }

      // dynamic import echart
      const echarts = await import('echarts/core');
      const {
        TooltipComponent,
        VisualMapComponent,
        TitleComponent,
        LegendComponent,
      } = await import('echarts/components');
      const { PieChart } = await import('echarts/charts');
      const { CanvasRenderer } = await import('echarts/renderers');

      echarts.use([
        TooltipComponent,
        VisualMapComponent,
        TitleComponent,
        LegendComponent,
        PieChart,
        CanvasRenderer,
      ]);

      const instance = echarts.init(chartRef.current!);
      chartInstance.current = instance;

      const colors = [
        '#00a6cb',
        '#00cc99',
        '#ffaa00',
        '#ee6666',
        '#339933',
        '#9966cc',
        '#ff66cc',
      ];

      const seriesData = Array.from(cityStats.entries()).map(
        ([key, value]) => ({
          value,
          name: t(`word.${key}`, key),
        }),
      );

      const option = {
        title: {
          text: t('word.PeaceAction', 'Peace Actions'),
          left: 'center',
          top: 0,
          textStyle: {
            fontFamily: 'Roboto',
            fontSize: 20,
            fontWeight: '400',
            color: '#032265',
          },
        },
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)',
          textStyle: {
            fontFamily: 'Roboto',
            fontSize: 14,
            color: '#032265',
          },
          backgroundColor: '#fff',
          borderColor: '#032265',
          borderWidth: 1,
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          top: 60,
          textStyle: {
            fontFamily: 'Roboto',
            fontSize: 14,
            color: 'var(--main-color)',
          },
        },
        color: colors,
        series: [
          {
            name: 'Actions',
            type: 'pie',
            radius: '60%',
            center: ['70%', '55%'],
            data: seriesData,
            label: {
              show: false,
              fontFamily: 'Roboto',
              fontSize: 14,
              color: '#032265',
            },
            itemStyle: {
              borderRadius: 4,
            },
            emphasis: {
              label: {
                fontSize: 16,
                fontWeight: 'bold',
              },
            },
          },
        ],
      };

      instance.setOption(option);
      setChartReady(true);
    };

    loadChart();

    return () => {
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [open, cityStats]);

  useEffect(() => {
    const handleResize = () => {
      chartInstance.current?.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  if (!open) return null;

  return (
    <Modal isOpen={open} onClose={onClose}>
      <div className={style.cityModal}>
        <h2 className={style.cityModaltitle}>{cityName}</h2>
        <div
          ref={chartRef}
          className={style.cityModalchart}
          style={{
            height: '250px',
            width: '100%',
            minWidth: '350px',
            visibility: chartReady ? 'visible' : 'hidden',
          }}
        />
        {!chartReady && <div className={style.absCenter}>Loading chart...</div>}
        {chartReady && cityStats && cityStats.size === 0 && (
          // check if cityStats is empty
          <div className={cl(style.noData, style.absCenter)}>
            {t('word.cityStatsEmpty', 'No point yet in this location')}
          </div>
        )}
      </div>
    </Modal>
  );
};
